import { useMemo, useRef } from "react";
import { fetchProposalDropdownOptions } from "../../../service/proposal/proposalService";

// RULES_JSON's dependent-fetch params are resolved to a flat object by the
// time fetchOptions sees them; by convention the static owner-entityType
// literal is aliased "parentType" and the TARGET field's own entityType (what
// kind of options this fetch resolves, e.g. "CTY") is aliased "type" (see
// JSON.js's "City depends on State" rules) — whatever other key is present is
// the dynamic value coming from the parent field itself. This holds for any
// future dependent rule following the same authoring convention, so no
// per-type branch is needed here. Warns instead of guessing if a rule ever
// supplies more than one candidate key, since silently picking one would be
// wrong data with no visible signal.
const getOwnerValueFromParams = (params = {}) => {
  const candidateKeys = Object.keys(params).filter(
    (key) => key !== "parentType" && key !== "type",
  );
  if (candidateKeys.length > 1) {
    console.warn(
      "useCraftDataProvider: ambiguous dependent-fetch params, expected exactly one non-parentType/type key",
      params,
    );
  }
  return candidateKeys.length ? params[candidateKeys[0]] : undefined;
};

// Builds the CraftDataProvider the renderer calls into for every select's
// options — both independent ones (fetchEntityOptions, fired on mount) and
// rule-driven dependent ones (fetchOptions, fired once the rules engine
// resolves a fetch task for it). `parentType`/`parentId` come from
// customerData and scope the dropdown list exactly the way CustomSelect.jsx
// does today.
//
// The reference implementation memoizes these calls with react-query, which
// this app doesn't use; this promise cache serves the same purpose — the
// renderer may ask for the same entityType from several fields, and without it
// each one would fire its own request. Caching the promise (not the result)
// also dedupes calls that are still in flight. Failures are evicted so a
// transient error doesn't poison the field for the rest of the session.
const useCraftDataProvider = (parentType, parentId) => {
  const cacheRef = useRef(new Map());

  return useMemo(() => {
    const cache = cacheRef.current;

    const cached = (key, fetcher) => {
      if (!cache.has(key)) {
        cache.set(
          key,
          fetcher().catch((error) => {
            cache.delete(key);
            throw error;
          }),
        );
      }
      return cache.get(key);
    };

    return {
      fetchEntityOptions: ({ entityType }) =>
        cached(`${parentType}|${parentId}|${entityType}`, () =>
          fetchProposalDropdownOptions({
            type: entityType,
            parentType,
            parentId,
          }),
        ),

      fetchOptions: (task) => {
        const type = task.params?.type;
        const ownerType = task.params?.parentType;
        const ownerValue = getOwnerValueFromParams(task.params);

        if (!type) {
          // Fails loud instead of silently posting type="undefined" to the
          // backend — the rule authoring the fetch must supply a
          // { value: "<TYPE>", as: "type" } param (see JSON.js) so this stays
          // generic across every dependent-select type, not just city.
          throw new Error(
            `useCraftDataProvider: rule for "${task.targetName}" is missing a { value: "<TYPE>", as: "type" } param in fetchOptions.params.`,
          );
        }

        return cached(
          `${parentType}|${parentId}|${type}|${ownerType}|${ownerValue}`,
          () =>
            fetchProposalDropdownOptions({
              type,
              parentType,
              parentId,
              ownerType,
              ownerValue,
            }),
        );
      },
    };
  }, [parentType, parentId]);
};

export default useCraftDataProvider;
